/**
 * CandidateDecisionWorkspace — the reader pane: one candidate's value, integrity, evidence and daemon findings with the
 * promote / retry / defer / reject actions. Demo page: /email (reading pane with actions).
 */
import { Link } from 'react-router-dom';
import { Check, ChevronRight, ExternalLink, Eye, RefreshCw, X } from 'lucide-react';
import { Badge, Button, Callout, EmptyState, Skeleton } from '../../../components/ui/index.js';
import { Card } from '../../../components/tailgrids/core/card.js';
import { sentenceCase } from '../../../components/ui/FilterSelect.js';
import { safeHref } from '../../../lib/safe-url.js';
import { daemonRunPath } from '../catalogReviewLinks.js';
import type { CatalogCandidateDecision } from './catalog-types.js';
import {
  buildCatalogSourceEvidenceHref,
  candidateLabel,
  candidateValueText,
  describeCatalogCandidateIntegrity,
  statusVariant,
} from './catalog-helpers.js';
import { EvidenceLinksSection } from './EvidenceLinksSection.js';
import { FindingCard } from './FindingCard.js';

export function CandidateDecisionWorkspace({
  candidate,
  loading,
  error,
  onPromote,
  onRetryReview,
  onReject,
  onDefer,
  busy,
}: {
  candidate: CatalogCandidateDecision | null;
  loading: boolean;
  error?: boolean;
  onPromote: () => void;
  onRetryReview: () => void;
  onReject: () => void;
  onDefer: () => void;
  busy: boolean;
}) {
  if (loading) {
    return (
      <Card>
        <Skeleton height={160} />
      </Card>
    );
  }
  if (error) {
    return (
      <Callout tone="danger" title="Could not load this candidate">
        Pick another candidate or try again in a moment.
      </Callout>
    );
  }
  if (!candidate) {
    return (
      <Card>
        <EmptyState
          icon={Eye}
          title="Select a candidate"
          description="Choose one candidate from the inbox to review daemon findings and decide what to do next."
        />
      </Card>
    );
  }

  const ready = candidate.status === 'ready_for_human' || candidate.status === 'approved';
  const failedReview = candidate.status === 'security_failed' || candidate.status === 'quality_failed';
  const findings =
    candidate.reviewStages?.flatMap((stage) =>
      stage.findings.map((finding) => ({ ...finding, daemonName: stage.daemonName })),
    ) ?? [];
  const reviewFindings = findings;
  const integrityState = describeCatalogCandidateIntegrity(candidate);
  const sourceEvidenceHref = buildCatalogSourceEvidenceHref(candidate.evidenceLinks, candidate.source);
  const hasSourceActions = Boolean(sourceEvidenceHref);

  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b bg-surface-alt p-4 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-heading">{candidateLabel(candidate)}</h2>
            <Badge variant={statusVariant(candidate.status)}>{sentenceCase(candidate.status)}</Badge>
            <Badge variant="neutral">{sentenceCase(candidate.targetType)}</Badge>
          </div>
          <p className="text-sm text-muted mt-2 max-w-3xl">
            {candidate.diffSummary ?? 'No discovery summary supplied.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="success" disabled={!ready || busy} onClick={onPromote}>
            <Check size={14} /> Promote
          </Button>
          {failedReview && (
            <Button size="sm" variant="secondary" disabled={busy} onClick={onRetryReview}>
              <RefreshCw size={14} /> Retry review
            </Button>
          )}
          <Button size="sm" variant="secondary" disabled={busy} onClick={onDefer}>
            Defer
          </Button>
          <Button size="sm" variant="danger" disabled={busy} onClick={onReject}>
            <X size={14} /> Reject
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {(integrityState.hasBlockingChecks ||
          integrityState.isSourceOnly ||
          integrityState.warningChecks.length > 0) && (
          <section
            className={`rounded-lg border p-4 ${
              integrityState.hasBlockingChecks
                ? 'border-warning/40 bg-warning-muted/30'
                : 'border-accent/20 bg-accent/5'
            }`}
          >
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold text-heading">Discovery integrity</h3>
                  {integrityState.badgeLabel && (
                    <Badge variant={integrityState.hasBlockingChecks ? 'warning' : 'info'}>
                      {integrityState.badgeLabel}
                    </Badge>
                  )}
                </div>
                <p className="mt-2 text-sm text-body">{integrityState.summary}</p>
                {integrityState.recommendation && (
                  <p className="mt-2 text-sm text-muted">Next step: {integrityState.recommendation}</p>
                )}
              </div>
              {hasSourceActions && (
                <div className="flex flex-wrap items-center gap-2">
                  {sourceEvidenceHref && (
                    <a
                      href={safeHref(sourceEvidenceHref) ?? undefined}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline"
                    >
                      Open source evidence <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              )}
            </div>
            {(integrityState.blockingChecks.length > 0 || integrityState.warningChecks.length > 0) && (
              <div className="mt-3 space-y-2">
                {[...integrityState.blockingChecks, ...integrityState.warningChecks.slice(0, 1)].map((check) => (
                  <div
                    key={check.id ?? `${check.checkType}-${check.checkedAt ?? check.message}`}
                    className="rounded-sm border bg-surface px-3 py-2"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={check.status === 'blocked' ? 'danger' : 'warning'}>{check.status}</Badge>
                      <span className="text-sm font-medium text-heading">{check.message}</span>
                    </div>
                    {check.recommendation && <div className="mt-1 text-xs text-muted">{check.recommendation}</div>}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-lg border bg-surface-alt p-4">
            <h3 className="text-sm font-semibold text-heading">What we found</h3>
            <p className="text-sm text-body mt-2">{candidate.diffSummary ?? candidateLabel(candidate)}</p>
            {buildCatalogSourceEvidenceHref(null, candidate.source) && (
              <a
                className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline mt-3"
                href={safeHref(buildCatalogSourceEvidenceHref(null, candidate.source)) ?? undefined}
                target="_blank"
                rel="noreferrer"
              >
                Open source <ExternalLink size={14} />
              </a>
            )}
          </div>
          <div className="rounded-lg border bg-surface-alt p-4">
            <h3 className="text-sm font-semibold text-heading">Why it is useful</h3>
            <p className="text-sm text-body mt-2">{candidateValueText(candidate)}</p>
          </div>
        </section>

        <EvidenceLinksSection candidate={candidate} />

        <section className="rounded-lg border bg-surface-alt p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-heading">Daemon review results</h3>
            <Badge variant={reviewFindings.length > 0 ? 'danger' : ready ? 'success' : 'warning'}>
              {reviewFindings.length > 0
                ? `${reviewFindings.length} finding${reviewFindings.length === 1 ? '' : 's'}`
                : sentenceCase(candidate.status)}
            </Badge>
          </div>
          <div className="mt-3 space-y-3">
            {candidate.reviewStages && candidate.reviewStages.length > 0 ? (
              candidate.reviewStages.map((stage) => {
                const stageReviewFindings = stage.findings;
                return (
                  <div key={stage.daemonName} className="rounded-sm border bg-surface p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-heading">{stage.daemonName}</span>
                      <Badge
                        variant={
                          stageReviewFindings.length > 0 || stage.lastError
                            ? 'danger'
                            : stage.status === 'completed'
                              ? 'success'
                              : 'warning'
                        }
                        size="sm"
                      >
                        {stageReviewFindings.length > 0 ? 'Findings' : sentenceCase(stage.status)}
                      </Badge>
                    </div>
                    {stage.lastError && <div className="text-sm text-danger mt-2">{stage.lastError}</div>}
                    {stage.daemonId && stage.runIds && stage.runIds.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        {stage.runIds.map((runId) => (
                          <Link
                            key={runId}
                            to={daemonRunPath(stage.daemonId!, runId)}
                            className="inline-flex items-center gap-1 text-accent-text hover:underline"
                          >
                            Run {String(runId).slice(0, 8)} <ChevronRight size={12} />
                          </Link>
                        ))}
                      </div>
                    )}
                    {stageReviewFindings.length === 0 && !stage.lastError ? (
                      <div className="text-sm text-muted mt-2">No findings recorded for this review stage.</div>
                    ) : (
                      <div className="mt-3 space-y-2">
                        {stageReviewFindings.map((finding) => (
                          <FindingCard key={finding.id} finding={finding} nextStepLabel="Recommendation" />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-muted">No daemon review results have been recorded yet.</div>
            )}
          </div>
        </section>

        <details className="rounded-lg border bg-surface-alt">
          <summary className="cursor-pointer p-3 text-sm font-medium text-heading">Raw candidate payload</summary>
          <pre className="text-xs border-t p-3 overflow-auto max-h-80">
            {JSON.stringify(candidate.payload, null, 2)}
          </pre>
        </details>
      </div>
    </Card>
  );
}
